import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { StorageService } from '../services/storage.service';
import { from, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Auth0Service } from 'src/app/auth/auth0.service';
import { StorageType } from '../models/constants';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Auth0HttpInterceptor implements HttpInterceptor {
  constructor(
    private auth: Auth0Service,
    private storageService: StorageService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only attach auth headers to own-domain API requests
    if (!this.isAllowedUrl(req.url)) {
      return next.handle(req);
    }

    const token = this.storageService.get(StorageType.auth0Token);
    if (token) {
      const tokenReq = this.cloneRequest(req, token);
      return next.handle(tokenReq);
    }

    // No token in storage. If the browser shows evidence of an Auth0
    // session (the SDK's marker cookie), the token was lost to a race
    // (logout in another tab, storage clear, expiry) — recover it silently
    // instead of firing an unauthenticated request that 401s (MDG-37).
    // Without that evidence this is genuinely public traffic (landing,
    // registration, pre-login config) and passes through unchanged.
    if (this.auth.hasIsAuthedCookie()) {
      return this.getTokenSilently(req, next);
    }
    return next.handle(this.cloneRequest(req));
  }

  // Single in-flight silent-token request shared by all concurrent
  // interceptions — without this, N simultaneous boot requests would
  // spawn N Auth0 iframe authorizations.
  private silentTokenPromise: Promise<string> | null = null;

  getTokenSilently(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.silentTokenPromise) {
      this.silentTokenPromise = this.auth
        .getTokenSilently$()
        .toPromise()
        .finally(() => (this.silentTokenPromise = null));
    }
    return from(this.silentTokenPromise).pipe(
      switchMap(token => {
        this.storageService.set(StorageType.auth0Token, token);
        const tokenReq = this.cloneRequest(req, token);
        return next.handle(tokenReq);
      }),
      catchError(() => {
        // Silent auth failed — the Auth0 session is gone. Clear the stale
        // marker so subsequent requests skip this path, and fall back to
        // the unauthenticated request (pre-existing behavior).
        this.auth.deleteIsAuthedCookie();
        const tokenReq = this.cloneRequest(req);
        return next.handle(tokenReq);
      }),
    );
  }

  /**
   * Determine if a URL should receive auth headers.
   * Only same-origin (relative) URLs and known API base URLs are allowed.
   */
  private isAllowedUrl(url: string): boolean {
    // Relative URLs (e.g. /api/...) are same-origin — always allowed
    if (url.startsWith('/')) {
      return true;
    }

    // Check against known API base URLs from environment
    const allowedBases = [
      environment.api_url_v1,
      environment.api_url_v2,
      environment.singleton_service_url,
    ].filter(Boolean);

    return allowedBases.some(base => url.startsWith(base));
  }

  cloneRequest(req, token = null) {
    if (token) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          SourceDomain: window.location.hostname,
          'Auth0-Client-Id': environment.auth0.clientId,
          'Hydra-Enabled': environment.hydraEnabled ? 'true' : 'false',
          'Client-Name-Space': environment.supplierConfig.clientNameSpace,
          CatalogType:
            environment.catalogType[window.location.hostname] || 'medigi',
        },
        withCredentials: true,
      });
    } else {
      return req.clone({
        setHeaders: {
          SourceDomain: window.location.hostname,
          'Auth0-Client-Id': environment.auth0.clientId,
          'Hydra-Enabled': environment.hydraEnabled ? 'true' : 'false',
          'Client-Name-Space': environment.supplierConfig.clientNameSpace,
          CatalogType:
            environment.catalogType[window.location.hostname] || 'medigi',
        },
        withCredentials: true,
      });
    }
  }
}
